import { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.bearing-intelligence.com';
const STORAGE_KEY = 'bi_demographics_submitted';

export function useDemographics() {
  const [submitted, setSubmitted] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');
  const markSubmitted = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setSubmitted(true);
  };
  return { submitted, markSubmitted };
}

const Field = ({ label, value, onChange, options }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ color: '#c4a563', fontSize: 11, fontWeight: 'bold', letterSpacing: '0.5px', marginBottom: 7 }}>
      {label}
    </div>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)} style={{
          padding: '5px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12,
          backgroundColor: value === opt.value ? 'rgba(196,165,99,0.18)' : '#0f1419',
          border: `1.5px solid ${value === opt.value ? '#c4a563' : 'rgba(255,255,255,0.1)'}`,
          color: value === opt.value ? '#c4a563' : '#777',
          transition: 'all 0.15s', fontFamily: 'inherit',
        }}>
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

export function DemographicsModal({ onComplete }) {
  const [data, setData] = useState({
    age_range: '', birth_sex: '', education: '',
    married: '', children: '', employment: '', region: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 2 steps for cleaner mobile layout

  const set = (key) => (val) => setData(d => ({ ...d, [key]: val }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/demographics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
    } catch(e) {
      // Silently fail — data is optional, never block the user
    } finally {
      setSubmitting(false);
      onComplete();
    }
  };

  const handleSkip = () => onComplete();

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 999,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        backgroundColor: '#0f1419',
        border: '1px solid rgba(196,165,99,0.3)',
        borderRadius: 12, maxWidth: 520, width: '100%',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid rgba(196,165,99,0.12)', flexShrink: 0 }}>
          <div style={{ color: '#c4a563', fontFamily: 'Georgia,serif', fontSize: 17, fontWeight: 'bold', marginBottom: 5 }}>
            Optional: Help Us Understand Our Readers
          </div>
          <div style={{ color: '#555', fontSize: 12, lineHeight: 1.6 }}>
            Completely anonymous — no names, no emails, no location tracking.
            This follows US Census data categories and cannot be linked to your identity.
            You may skip any question or the entire survey.
          </div>
          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
            {[1,2].map(s => (
              <div key={s} style={{ height: 3, flex: 1, borderRadius: 2,
                backgroundColor: step >= s ? '#c4a563' : 'rgba(255,255,255,0.1)' }} />
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
          {step === 1 && (
            <>
              <Field label="AGE RANGE" value={data.age_range} onChange={set('age_range')} options={[
                { value:'18-24', label:'18–24' }, { value:'25-34', label:'25–34' },
                { value:'35-44', label:'35–44' }, { value:'45-54', label:'45–54' },
                { value:'55-64', label:'55–64' }, { value:'65+', label:'65+' },
                { value:'prefer_not', label:'Prefer not to say' },
              ]} />
              <Field label="BIRTH SEX" value={data.birth_sex} onChange={set('birth_sex')} options={[
                { value:'male', label:'Male' }, { value:'female', label:'Female' },
                { value:'prefer_not', label:'Prefer not to say' },
              ]} />
              <Field label="HIGHEST EDUCATION LEVEL" value={data.education} onChange={set('education')} options={[
                { value:'high_school', label:'High school' },
                { value:'some_college', label:'Some college' },
                { value:'bachelors', label:"Bachelor's" },
                { value:'graduate', label:'Graduate degree' },
                { value:'prefer_not', label:'Prefer not to say' },
              ]} />
              <Field label="REGION" value={data.region} onChange={set('region')} options={[
                { value:'northeast', label:'Northeast' }, { value:'midwest', label:'Midwest' },
                { value:'south', label:'South' }, { value:'west', label:'West' },
                { value:'outside_us', label:'Outside US' },
                { value:'prefer_not', label:'Prefer not to say' },
              ]} />
            </>
          )}
          {step === 2 && (
            <>
              <Field label="MARITAL STATUS" value={data.married} onChange={set('married')} options={[
                { value:'yes', label:'Married' }, { value:'no', label:'Not married' },
                { value:'prefer_not', label:'Prefer not to say' },
              ]} />
              <Field label="CHILDREN" value={data.children} onChange={set('children')} options={[
                { value:'none', label:'None' }, { value:'1-2', label:'1–2' },
                { value:'3-4', label:'3–4' }, { value:'5+', label:'5+' },
                { value:'prefer_not', label:'Prefer not to say' },
              ]} />
              <Field label="EMPLOYMENT STATUS" value={data.employment} onChange={set('employment')} options={[
                { value:'employed', label:'Employed' },
                { value:'self_employed', label:'Self-employed' },
                { value:'retired', label:'Retired' },
                { value:'student', label:'Student' },
                { value:'other', label:'Other' },
                { value:'prefer_not', label:'Prefer not to say' },
              ]} />
              <div style={{ marginTop: 6, padding: '10px 12px', backgroundColor: 'rgba(196,165,99,0.04)',
                borderRadius: 6, border: '1px solid rgba(196,165,99,0.1)', color: '#444', fontSize: 11, lineHeight: 1.6 }}>
                Your responses are stored anonymously with a random identifier. They cannot be linked
                to your account, email, or any personal information. Aggregate data only is used
                to understand our readership.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 22px 18px', borderTop: '1px solid rgba(196,165,99,0.1)', flexShrink: 0,
          display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={handleSkip} style={{
            background: 'none', border: 'none', color: '#444', cursor: 'pointer', fontSize: 12,
          }}>
            {step === 1 ? 'Skip survey' : 'Skip remaining'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step === 2 && (
              <button onClick={() => setStep(1)} style={{
                padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 12,
                backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#666',
              }}>← Back</button>
            )}
            {step === 1 ? (
              <button onClick={() => setStep(2)} style={{
                padding: '8px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold',
                backgroundColor: 'rgba(196,165,99,0.15)', border: '1px solid #c4a563', color: '#c4a563',
              }}>Next →</button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: '8px 20px', borderRadius: 6, cursor: submitting ? 'wait' : 'pointer',
                fontSize: 12, fontWeight: 'bold',
                backgroundColor: 'rgba(196,165,99,0.15)', border: '1px solid #c4a563', color: '#c4a563',
              }}>{submitting ? 'Submitting…' : 'Submit'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
